import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
const { FiUsers, FiSettings, FiCalendar, FiMessageSquare, FiImage, FiStar, FiShield, FiTerminal, FiLayout } = FiIcons;
import SafeIcon from '../../common/SafeIcon';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/auth/me', { credentials: 'include' });
        const data = await response.json();
        if (data.status === 'success') {
          setUser(data.user);
          // Redirection for developer
          if (data.user.role === 'developer') {
            navigate('/developer');
          }
        }
      } catch (err) {
        console.error('Auth check failed');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;

  const isSuperAdmin = user?.role === 'super_admin';

  const menuItems = [
    { label: 'Bookings', icon: FiCalendar, path: '/admin/bookings', permission: 'bookings.view' },
    { label: 'Messages', icon: FiMessageSquare, path: '/admin/messages', permission: 'messages.view' },
    { label: 'Gallery', icon: FiImage, path: '/admin/gallery', permission: 'gallery.view' },
    { label: 'Testimonials', icon: FiStar, path: '/admin/testimonials', permission: 'testimonials.view' },
    { label: 'Content', icon: FiSettings, path: '/admin/content', permission: 'content.view' },
  ];

  // Filter menu items for temporary admins
  const displayedItems = user?.role === 'temporary_admin' 
    ? menuItems.filter(item => {
        const perms = typeof user.permissions === 'string' ? JSON.parse(user.permissions || "[]") : (user.permissions || []);
        return perms.includes(item.permission);
      })
    : menuItems;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">Welcome back, <span className="font-semibold text-blue-600">{user?.display_name}</span></p>
        </div>
        <div className="flex flex-wrap gap-4">
          {isSuperAdmin && (
            <Link to="/admin/access-management" className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-blue-700 transition shadow-lg shadow-blue-200">
              <SafeIcon icon={FiShield} />
              Access Management
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayedItems.map((item) => (
          <Link 
            key={item.label}
            to={item.path}
            className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
          >
            <div className="flex items-center gap-5">
              <div className="p-4 bg-blue-50 text-blue-600 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                <SafeIcon icon={item.icon} className="text-3xl" />
              </div>
              <div>
                <span className="text-xl font-bold text-gray-900 block">{item.label}</span>
                <span className="text-sm text-gray-500">Manage your {item.label.toLowerCase()}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      
      {displayedItems.length === 0 && (
        <div className="bg-white p-12 rounded-2xl shadow-sm border border-gray-100 text-center">
          <div className="p-4 bg-gray-50 text-gray-400 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiLayout} className="text-2xl" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No Access Granted</h3>
          <p className="text-gray-500">You don't have permission to access any management modules.</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;